import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TableCell,
    TableRow,
    TableSortLabel,
    CircularProgress,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    Chip,
    Popover,
    Divider,
    Typography,
} from '@mui/material';
import { Delete, Add as AddIcon } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import notificationService from '../../../shared/services/notificationService';
import { NotificationAttributes } from '../../../shared/types/notification';
import moment from 'moment';

export const Notifications: React.FC = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [totalRows, setTotalRows] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [notifications, setNotifications] = useState<NotificationAttributes[]>([]);
    const [sortData, setSortData] = useState<{ active: string; direction: 'asc' | 'desc' } | null>(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'info' | 'success' | 'error' });
    const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [selectedNotificationId, setSelectedNotificationId] = useState<string | null>(null);

    const fetchNotifications = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await notificationService.getNotifications({
                page: currentPage + 1,
                limit: pageSize
            });

            if (response.success) {
                const notifyData = response.data;
                let list = notifyData?.data || [];

                if (searchInput) {
                    const searchLower = searchInput.toLowerCase();
                    list = list.filter(
                        (n) =>
                            n.title.toLowerCase().includes(searchLower) ||
                            n.message.toLowerCase().includes(searchLower)
                    );
                }

                setNotifications(list);
                setTotalRows(notifyData?.pagination?.totalItems || list.length);
            }
        } catch (error: any) {
            console.error('Error fetching notifications:', error);
            setSnackbar({ open: true, message: 'Error loading notifications', severity: 'error' });
            setNotifications([]);
            setTotalRows(0);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, pageSize, searchInput]);

    useEffect(() => {
        fetchNotifications();
    }, [currentPage, pageSize]);

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        setCurrentPage(0);
    };

    const handleSort = (property: string) => {
        const isAsc = sortData?.active === property && sortData?.direction === 'asc';
        setSortData({
            active: property,
            direction: isAsc ? 'desc' : 'asc'
        });
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage - 1);
    };

    const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
        setDeleteAnchorEl(event.currentTarget);
        setSelectedNotificationId(id);
    };

    const handleDeleteClose = () => {
        setDeleteAnchorEl(null);
        setSelectedNotificationId(null);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedNotificationId) return;

        try {
            const response = await notificationService.deleteNotification(selectedNotificationId);
            if (response.success) {
                setSnackbar({ open: true, message: 'Notification deleted successfully', severity: 'success' });
                fetchNotifications();
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
            setSnackbar({ open: true, message: 'Failed to delete notification', severity: 'error' });
        } finally {
            handleDeleteClose();
        }
    };

    const sortedNotifications = [...notifications].sort((a, b) => {
        if (!sortData?.active) return 0;
        const key = sortData.active as keyof NotificationAttributes;
        const aVal = (a[key] || '').toString().toLowerCase();
        const bVal = (b[key] || '').toString().toLowerCase();
        if (sortData.direction === 'asc') {
            return aVal.localeCompare(bVal);
        }
        return bVal.localeCompare(aVal);
    });

    return (
        <div className="flex flex-col w-full min-h-full bg-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b border-gray-200 bg-white">
                {isLoading && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <CircularProgress />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                        Notifications
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <AppSearch
                        value={searchInput}
                        onChange={handleSearchChange}
                        onSearch={fetchNotifications}
                        placeholder="Search"
                        className="w-full sm:w-64"
                    />
                    <AppButton
                        variant="primary"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/notifications/send')}
                    >
                        Add Notification
                    </AppButton>
                </div>
            </div>

            {/* Delete Confirmation Popover */}
            <Popover
                open={Boolean(deleteAnchorEl)}
                anchorEl={deleteAnchorEl}
                onClose={handleDeleteClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        p: 0,
                        mt: 1.5,
                        width: 250,
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                    },
                }}
            >
                <div className="p-4 bg-white">
                    <Typography className="font-semibold text-gray-900" sx={{ mb: 1 }}>
                        Are you sure?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        This notification will be permanently deleted.
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <div className="flex justify-end gap-2">
                        <AppButton
                            variant="secondary"
                            size="small"
                            onClick={handleDeleteClose}
                        >
                            Cancel
                        </AppButton>
                        <AppButton
                            variant="danger"
                            size="small"
                            onClick={handleDeleteConfirm}
                        >
                            Delete
                        </AppButton>
                    </div>
                </div>
            </Popover>

            <div className="flex flex-1 flex-col p-6 sm:p-10 overflow-y-auto">
                <div className="flex flex-col w-full flex-1">
                    <AppTable
                        head={
                            <TableRow className="bg-gray-100">
                                <TableCell className="!font-semibold !text-gray-700">No</TableCell>
                                <TableCell className="!font-semibold !text-gray-700">
                                    <TableSortLabel
                                        active={sortData?.active === 'title'}
                                        direction={sortData?.active === 'title' ? sortData.direction : 'asc'}
                                        onClick={() => handleSort('title')}
                                    >
                                        Title
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell className="!font-semibold !text-gray-700">Type</TableCell>
                                <TableCell className="!font-semibold !text-gray-700">Audience</TableCell>
                                <TableCell className="!font-semibold !text-gray-700">Created At</TableCell>
                                <TableCell className="!font-semibold !text-gray-700" width={100}>Actions</TableCell>
                            </TableRow>
                        }
                        isEmpty={sortedNotifications.length === 0}
                        emptyColSpan={6}
                    >
                        {sortedNotifications.map((item, i) => (
                            <TableRow
                                key={item.id}
                                className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                            >
                                <TableCell className="!text-gray-700">{(currentPage * pageSize) + i + 1}</TableCell>
                                <TableCell className="!text-gray-900">
                                    <div className="flex flex-col">
                                        <span className="font-semibold">{item.title}</span>
                                        <span className="text-sm text-gray-500 line-clamp-1">{item.message}</span>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={item.type.toUpperCase()}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell className="!text-gray-700">{item.targetAudience}</TableCell>
                                <TableCell className="!text-gray-700">
                                    {moment(item.createdAt).format('DD MMM YYYY HH:mm')}
                                </TableCell>
                                <TableCell>
                                    <Tooltip title="Delete">
                                        <IconButton onClick={(e) => handleDeleteClick(e, item.id)} color="error" size="small">
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </AppTable>
                </div>

                <AppTablePagination
                    totalRows={totalRows}
                    page={currentPage}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={(size) => setPageSize(size)}
                />
            </div>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
            </Snackbar>
        </div>
    );
};
